import React, { useEffect } from 'react';
import PlanSelector from '../components/subscription/PlanSelector';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import './Subscription.css';

const Subscription: React.FC = () => {
  const { plan, planName, usage, refreshPlan, refreshUsage, isLoading } = useSubscription();
  const { user } = useAuth();

  useEffect(() => {
    refreshPlan();
    refreshUsage();
  }, []);

  const getPlanIcon = () => {
    switch (planName) {
      case 'free': return '🆓';
      case 'premium': return '⭐';
      case 'unlimited': return '💎';
      default: return '📋';
    }
  };

  if (isLoading) {
    return (
      <div className="subscription-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando información de suscripción...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Tu Suscripción</h1>
        <p>Gestiona tu plan y visualiza tu uso</p>
      </div>

      {/* Current Plan Info */}
      {plan && (
        <div className="current-subscription">
          <h2>Tu Plan Actual</h2>
          <div className={`subscription-card ${planName}`}>
            <div className="subscription-info">
              <div className="plan-details">
                <span className="plan-icon-large">{getPlanIcon()}</span>
                <h3>{plan.display_name}</h3>
                <div className="plan-price">
                  {plan.price === 0 ? 'Gratis' : `${plan.price}€/mes`}
                </div>
                <div className="plan-status">
                  <span className="status-badge active">Activo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats */}
      {usage && (
        <div className="usage-section">
          <h2>Tu Uso Este Mes</h2>
          <div className="usage-grid">
            <div className="usage-card">
              <div className="usage-icon">🤕</div>
              <div className="usage-info">
                <span className="usage-label">Dolores de Cabeza</span>
                <span className="usage-value">
                  {usage.usage.headaches.current}
                  {usage.usage.headaches.limit !== -1 && (
                    <span className="usage-limit"> / {usage.usage.headaches.limit}</span>
                  )}
                </span>
                {usage.usage.headaches.limit === -1 && (
                  <span className="usage-unlimited">Ilimitado</span>
                )}
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-icon">💓</div>
              <div className="usage-info">
                <span className="usage-label">Tensiones</span>
                <span className="usage-value">
                  {usage.usage.tensions.current}
                  {usage.usage.tensions.limit !== -1 && (
                    <span className="usage-limit"> / {usage.usage.tensions.limit}</span>
                  )}
                </span>
                {usage.usage.tensions.limit === -1 && (
                  <span className="usage-unlimited">Ilimitado</span>
                )}
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-icon">😢</div>
              <div className="usage-info">
                <span className="usage-label">Lloros</span>
                <span className="usage-value">
                  {usage.usage.lloros.current}
                  {usage.usage.lloros.limit !== -1 && (
                    <span className="usage-limit"> / {usage.usage.lloros.limit}</span>
                  )}
                </span>
                {usage.usage.lloros.limit === -1 && (
                  <span className="usage-unlimited">Ilimitado</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Features */}
      {plan && (
        <div className="plan-benefits">
          <h2>Beneficios de tu Plan</h2>
          <div className="benefits-grid">
            {Object.entries(plan.features).map(([feature, enabled]) => (
              <div key={feature} className={`benefit-item ${enabled ? 'enabled' : 'disabled'}`}>
                <span className="benefit-icon">
                  {enabled ? '✅' : '❌'}
                </span>
                <span className="benefit-text">
                  {feature === 'basic_tracking' && 'Seguimiento básico'}
                  {feature === 'calendar_view' && 'Vista de calendario'}
                  {feature === 'advanced_analytics' && 'Analíticas avanzadas'}
                  {feature === 'export_data' && 'Exportar datos'}
                  {feature === 'priority_support' && 'Soporte prioritario'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan Comparison */}
      <div className="plan-selection">
        <h2>Comparar Planes</h2>
        <PlanSelector />
      </div>
    </div>
  );
};

export default Subscription;
